import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import { TGuidanceApiData, TGuidancePatchPayload } from "./guidance.types";
import {
  TProfileData,
  TProfileResponsePayload,
  TRequestEmailChangePayload,
  TRole,
  TUpdateProfileRequestPayload,
} from "./user.types";

import { TApiResponse } from "../../types/api";
import { handleAsync } from "../../utils";
import { withAuthHooks } from "../auth/auth.ky.hooks";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class UserService {
  private rootEndpoint = "profile" as const;

  async getProfile(options?: TApiServiceOptions) {
    const result = await handleAsync<TApiServiceError, TApiResponse<TProfileResponsePayload["data"]>>(() =>
      apiService.get<TApiResponse<TProfileResponsePayload["data"]>>(this.rootEndpoint, options)
    );

    // Extract just the data field from the response
    if (result.data) {
      return {
        data: result.data.data,
        error: null,
      };
    }

    return result;
  }

  updateUser(payload: TUpdateProfileRequestPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TProfileData>>(() =>
      apiService.patch(this.rootEndpoint, payload, options)
    );
  }

  deleteProfile(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.delete(this.rootEndpoint, undefined, options)
    );
  }

  async getRoles(options?: TApiServiceOptions) {
    const result = await handleAsync<TApiServiceError, TApiResponse<TRole[]>>(() =>
      apiService.get<TApiResponse<TRole[]>>(`${this.rootEndpoint}/roles`, options)
    );

    // Extract just the data field from the response
    if (result.data) {
      return {
        data: result.data.data,
        error: null,
      };
    }

    return result;
  }

  getConsents(options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<
        {
          consent_id: string;
          user_id: string;
          consent_type: string;
          consent_value: boolean;
          timestamp: string;
          policy_version: string;
          ip_address?: string | null;
          user_agent?: string | null;
        }[]
      >
    >(() => apiService.get(`${this.rootEndpoint}/consents`, options));
  }

  recordConsent(
    payload: {
      user_id: string;
      consent_type: string;
      consent_value: boolean;
      policy_version: string;
      ip_address?: string | null;
      user_agent?: string | null;
    },
    options?: TApiServiceOptions
  ) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<{
        consent_id: string;
        user_id: string;
        consent_type: string;
        consent_value: boolean;
        timestamp: string;
        policy_version: string;
        ip_address?: string | null;
        user_agent?: string | null;
      }>
    >(() => apiService.post(`${this.rootEndpoint}/consents`, payload, options));
  }

  getAuditTrail(page = 1, pageSize = 50, options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<{ events: unknown[]; total: number; page: number; page_size: number }>
    >(() => apiService.get(`${this.rootEndpoint}/audit-trail?page=${page}&page_size=${pageSize}`, options));
  }

  requestPhoneNumberChange(payload: { phone_number: string }, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.post(`${this.rootEndpoint}/phone/change-request`, payload, options)
    );
  }

  requestEmailChange(payload: TRequestEmailChangePayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.post(`${this.rootEndpoint}/email/change-request`, payload, options)
    );
  }

  async getGuidance(options?: TApiServiceOptions) {
    const result = await handleAsync<TApiServiceError, TApiResponse<TGuidanceApiData>>(() =>
      apiService.get<TApiResponse<TGuidanceApiData>>(`${this.rootEndpoint}/guidance`, options)
    );

    if (result.data) {
      return {
        data: result.data.data,
        error: null,
      };
    }

    return result;
  }

  async patchGuidance(payload: TGuidancePatchPayload, options?: TApiServiceOptions) {
    const result = await handleAsync<TApiServiceError, TApiResponse<TGuidanceApiData>>(() =>
      apiService.patch<TApiResponse<TGuidanceApiData>>(`${this.rootEndpoint}/guidance`, payload, options)
    );

    if (result.data) {
      return {
        data: result.data.data,
        error: null,
      };
    }

    return result;
  }
}

export const userService = new UserService();
